;(function (angular, _) {
  'use strict'
  angular.module('kilo.common.services')
    .service('PennylaneService', PennylaneService)

  PennylaneService.$inject = [
    // Angular
    '$http',
    // Libs
    // Managers
    // Services
    // Const
    'urls'
    // Resolved
  ]

  /**
   * Client HTTP vers backend-pennylane (intégration Pennylane / facture électronique).
   * Calqué sur ChorusService : l'auth (Bearer 90tech) est injectée automatiquement
   * par le jwtInterceptor global — aucun header d'auth à gérer ici.
   * ⚠️ Ne jamais logger le code/state OAuth : ils ne transitent qu'en params.
   */
  function PennylaneService (// Angular
    $http,
    // Libs
    // Managers
    // Services
    // Const
    urls
    // Resolved
  ) {
    const self = this

    _.extend(self, {
      // Public Attributes
      // Public Method
      // Companies
      getCompanies,
      getCompany,
      createCompany,
      updateCompany,
      getConnectedCompany,
      getFreshCompany,
      // Users (dirigeant)
      getUsers,
      getMe,
      createUser,
      updateUser,
      // Provisioning Pennylane
      provision,
      // OAuth company-scoped
      getAuthorizeUrl,
      sendCallback,
      disconnect,
      // Statut e-invoicing entreprise
      getPaRegistrations,
      // Invoices
      getInvoices,
      getInvoice,
      syncReception,
      refreshReceptionInvoice,
      getInvoiceStatus,
      emitInvoices,
      checkInvoiceEmittable,
      // Statut e-invoicing plateforme (PA) + renvoi après rejet
      getEInvoicingStatusByKilo,
      reEmitInvoice,
      // Monitoring (suivi des traitements asynchrones)
      getMonitoring,
      getLastEmissionMonitoring
    })

    // Mémoïsation par session de getConnectedCompany : la promesse est mise en
    // cache à la 1re invocation puis réutilisée → 1 seul appel réseau par
    // session, partagé entre la fiche facture et la liste.
    let connectedCompanyPromise = null

    // ── Companies ────────────────────────────────────────────────────────────

    /** Liste des companies du tenant courant (tableau, vide si aucune). */
    function getCompanies () {
      return $http({
        url: `${urls.backendPennylane}/companies`,
        method: 'GET'
      })
    }

    function getCompany (id) {
      return $http({
        url: `${urls.backendPennylane}/companies/${id}`,
        method: 'GET'
      })
    }

    function createCompany (payload) {
      return $http({
        url: `${urls.backendPennylane}/companies`,
        method: 'POST',
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      })
    }

    function updateCompany (id, payload) {
      return $http({
        url: `${urls.backendPennylane}/companies/${id}`,
        method: 'PATCH',
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      })
    }

    /**
     * Company du tenant connectée à Pennylane, ou `null` sinon (aucune company,
     * company non connectée, erreur HTTP backend down/4xx…).
     * Ne rejette JAMAIS : sert à masquer des boutons, ne doit rien casser.
     * Mémoïsée par session (voir `connectedCompanyPromise` ci-dessus).
     *
     * ⚠️ Appel $http DÉDIÉ — on ne réutilise pas getCompanies (son toast
     * d'erreur global est voulu côté Préférences). Cette méthode est appelée
     * au $onInit du progressbar, composant partagé de TOUTES les fiches
     * facture/avoir pour TOUS les clients : un backend-pennylane indisponible
     * ne doit JAMAIS déclencher l'erreur globale plein écran (broadcast
     * `httpNotResponding` → `$rootScope.globalError`, jamais réinitialisée)
     * ni la déconnexion complète (403 « Autorisation refusée » →
     * zlAuth.disconnect). L'intercepteur (HttpErrorInterceptor) vérifie
     * `ignoreErrors` par _.includes → il faut ÉNUMÉRER chaque statut ;
     * -1/0 couvrent l'échec réseau (AngularJS met status -1 backend down).
     *
     * NB : cette liste vit à DEUX endroits (ici et getFreshCompany) et elles
     * DIFFÈRENT — ici la liste historique plus étroite (trou 504/429/408…
     * pré-existant, mémoïsée 1 req/session donc exposition minime, décision
     * utilisateur en attente) ; getFreshCompany utilise la liste LARGE
     * (rejouée à chaque $onInit de toolbar). Ne pas « harmoniser » à l'aveugle.
     */
    function getConnectedCompany () {
      if (!connectedCompanyPromise) {
        connectedCompanyPromise = $http({
          url: `${urls.backendPennylane}/companies`,
          method: 'GET',
          ignoreErrors: [-1, 0, 400, 401, 403, 404, 409, 422, 500, 502, 503]
        }).then((res) => {
          const company = res.data && res.data[0]
          if (company && company.pennylane && company.pennylane.connectionStatus === 'connected') {
            return company
          }
          return null
        }).catch(() => null)
      }
      return connectedCompanyPromise
    }

    /**
     * Première company du tenant (résolue à `null` si aucune / erreur HTTP),
     * lue FRAÎCHEMENT à chaque appel — contrairement à getConnectedCompany
     * (mémoïsée par session), pour les champs qui bougent pendant la session
     * comme `pennylane.receptionLastRunAt` (toolbar de l'inbox).
     * Ne rejette JAMAIS : sert à un affichage informatif, ne doit rien casser.
     *
     * ⚠️ Appelée au $onInit d'une toolbar (background), REJOUÉE à chaque
     * `$state.reload()` post-sync : mêmes raisons que getConnectedCompany
     * (voir son commentaire) — un backend-pennylane indisponible ne doit
     * JAMAIS déclencher le toast global plein écran ni la déconnexion (403) →
     * `ignoreErrors` ÉNUMÉRÉS, liste LARGE (celle de getMonitoring /
     * refreshReceptionInvoice : 504 gateway, 429 rate-limit… compris — un
     * simple 504 nginx au chargement masquerait l'app définitivement) ; -1/0
     * couvrent l'échec réseau (AngularJS met status -1 backend down).
     *
     * NB : la liste vit à DEUX endroits (ici et getConnectedCompany) et elles
     * DIFFÈRENT — getConnectedCompany garde sa liste historique plus étroite
     * (trou pré-existant, décision utilisateur en attente). Toute NOUVELLE
     * méthode doit prendre la liste LARGE.
     *
     * ⚠️ Écart VOULU avec getConnectedCompany : ne filtre PAS sur
     * `connectionStatus === 'connected'` — renvoie la company telle quelle
     * (l'appelant lit des champs informatifs, connectée ou non).
     */
    function getFreshCompany () {
      return $http({
        url: `${urls.backendPennylane}/companies`,
        method: 'GET',
        ignoreErrors: [-1, 0, 400, 401, 403, 404, 405, 408, 409, 410, 412, 422, 429, 500, 501, 502, 503, 504]
      }).then((res) => (res.data && res.data[0]) || null)
        .catch(() => null)
    }

    // ── Users (dirigeant, 1:1 avec la company du tenant) ─────────────────────

    /** Dirigeant de la company courante — 404 légitime tant qu'aucun n'est créé. */
    function getUsers () {
      return $http({
        url: `${urls.backendPennylane}/users`,
        method: 'GET',
        ignoreErrors: [404]
      })
    }

    /** Alias de getUsers côté backend — même 404 légitime. */
    function getMe () {
      return $http({
        url: `${urls.backendPennylane}/users/me`,
        method: 'GET',
        ignoreErrors: [404]
      })
    }

    function createUser (payload) {
      return $http({
        url: `${urls.backendPennylane}/users`,
        method: 'POST',
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      })
    }

    function updateUser (id, payload) {
      return $http({
        url: `${urls.backendPennylane}/users/${id}`,
        method: 'PATCH',
        data: payload,
        headers: { 'Content-Type': 'application/json' }
      })
    }

    // ── Provisioning Pennylane ───────────────────────────────────────────────

    /** Provisionne la company chez Pennylane (POST /users → POST /companies côté backend). */
    function provision (companyId) {
      return $http({
        url: `${urls.backendPennylane}/companies/${companyId}/pennylane/provision`,
        method: 'POST'
      })
    }

    // ── OAuth company-scoped ─────────────────────────────────────────────────

    /** Réponse : `{ authorizeUrl }` (URL d'autorisation OAuth Pennylane). */
    function getAuthorizeUrl (companyId) {
      return $http({
        url: `${urls.backendPennylane}/companies/${companyId}/pennylane/authorize-url`,
        method: 'GET'
      })
    }

    /**
     * Relaie le code OAuth reçu de Pennylane au backend (qui stocke les tokens).
     * Réponse : `{ connectionStatus }`.
     * ignoreErrors : l'écran de callback affiche son propre message d'échec —
     * on supprime le toast global sur les statuts d'erreur attendus du backend
     * (400 state invalide/expiré, 404 company, 409 gating, 502 Pennylane,
     * 503 Redis) ; la promesse reste rejetée (géré localement).
     */
    function sendCallback (companyId, code, state) {
      return $http({
        url: `${urls.backendPennylane}/companies/${companyId}/pennylane/callback`,
        method: 'GET',
        params: { code: code, state: state },
        ignoreErrors: [400, 404, 409, 502, 503]
      }).then((res) => {
        // Connexion réussie pendant la session : on invalide le cache de
        // getConnectedCompany pour que le bouton « Envoyer sur Pennylane »
        // apparaisse sans rechargement complet de l'application.
        connectedCompanyPromise = null
        return res
      })
    }

    /** Réponse : `{ connectionStatus: 'disconnected' }`. */
    function disconnect (companyId) {
      return $http({
        url: `${urls.backendPennylane}/companies/${companyId}/pennylane/disconnect`,
        method: 'POST'
      }).then((res) => {
        // Déconnexion pendant la session : on invalide le cache de
        // getConnectedCompany pour que le bouton disparaisse sans F5.
        connectedCompanyPromise = null
        return res
      })
    }

    // ── Statut e-invoicing entreprise ────────────────────────────────────────

    /**
     * Réponse : `{ registrations: [...], emissionAllowed }`.
     * ignoreErrors 409 : company non connectée à Pennylane = état légitime
     * (message dégradé affiché localement, pas de toast global).
     */
    function getPaRegistrations (companyId) {
      return $http({
        url: `${urls.backendPennylane}/companies/${companyId}/pennylane/pa-registrations`,
        method: 'GET',
        // Chargé automatiquement à l'ouverture des Préférences : on supprime le
        // toast global (message générique trompeur) — 409 = non connectée,
        // 422/502/503 = amont Pennylane indisponible ; l'encart dégradé de
        // l'onglet couvre déjà ces cas.
        ignoreErrors: [409, 422, 502, 503]
      })
    }

    // ── Invoices ─────────────────────────────────────────────────────────────

    /** @param params ex. `{ direction: 'supplier' }` (factures reçues). */
    function getInvoices (params) {
      return $http({
        url: `${urls.backendPennylane}/invoices`,
        method: 'GET',
        params: params
      })
    }

    function getInvoice (id) {
      return $http({
        url: `${urls.backendPennylane}/invoices/${id}`,
        method: 'GET'
      })
    }

    /** Synchronisation des factures d'achat depuis Pennylane (réception synchrone). */
    function syncReception () {
      return $http({
        url: `${urls.backendPennylane}/invoices/reception/sync`,
        method: 'POST',
        // 404 (aucune company) / 409 (non connectée à Pennylane) : états
        // fonctionnels expliqués par une snack dédiée côté toolbar — le toast
        // global générique (« vérifier la connexion internet ») serait faux.
        ignoreErrors: [404, 409]
      })
    }

    /**
     * Resynchronise UNE facture reçue depuis Pennylane
     * (`POST /invoices/:id/reception/refresh`). Réponse : la facture locale à
     * jour (mêmes champs que la liste). Erreurs attendues : 404 (introuvable /
     * company non connectée), 409 (non synchronisée avec Pennylane OU
     * rafraîchissement déjà en cours — message FR dans `err.data.message`),
     * 502 (amont Pennylane).
     *
     * Déclenché par un CLIC utilisateur : les erreurs sont restituées en snack
     * par la liste — on supprime le toast global plein écran (et le logout 403)
     * sur une énumération LARGE (mêmes statuts élargis que getMonitoring :
     * 429 rate-limit, 504 gateway… ; -1/0 = échec réseau) ; la promesse reste
     * REJETÉE pour que l'appelant affiche le message dédié.
     * @param invoiceId `_id` de la facture locale (backend-pennylane)
     */
    function refreshReceptionInvoice (invoiceId) {
      return $http({
        url: `${urls.backendPennylane}/invoices/${invoiceId}/reception/refresh`,
        method: 'POST',
        ignoreErrors: [-1, 0, 400, 401, 403, 404, 405, 408, 409, 410, 412, 422, 429, 500, 501, 502, 503, 504]
      })
    }

    /** Statut LIVE de la facture chez Pennylane (normalisé par le backend). */
    function getInvoiceStatus (id) {
      return $http({
        url: `${urls.backendPennylane}/invoices/${id}/pennylane-status`,
        method: 'GET'
      })
    }

    /**
     * Émission de factures Kilo vers Pennylane (traitement asynchrone côté
     * backend). Réponse : `{ accepted: N, items: [{ kiloInvoiceId, monitoringId }] }`
     * (`items` absent si le backend n'est pas encore redéployé — les appelants
     * doivent le tolérer).
     * @param kiloInvoiceIds tableau d'ids de factures Kilo (`_id` webapp).
     */
    function emitInvoices (kiloInvoiceIds) {
      return $http({
        url: `${urls.backendPennylane}/invoices/emit`,
        method: 'POST',
        data: { kiloInvoiceIds: kiloInvoiceIds },
        headers: { 'Content-Type': 'application/json' }
      })
    }

    // ── Contrôle pré-émission ────────────────────────────────────────────────

    /**
     * Contrôle côté front des données client qu'exigera backend-pennylane à
     * l'émission (miroir des gardes de kilo-invoice.service.ts backend, pour
     * prévenir les échecs plutôt que les subir). Fonction PURE : aucun appel
     * réseau, appelable en masse sur une sélection.
     *
     * - `blocking` : `client` absent ou non peuplé (pas un objet) → le backend
     *   rejettera systématiquement (422 « Facture Kilo sans client
     *   exploitable ») → ne pas envoyer.
     * - `warnings` : champs requis par Pennylane uniquement à la CRÉATION du
     *   customer (nom B2B, prénom/nom B2C, adresse de facturation complète).
     *   La webapp ne peut pas savoir si le customer existe déjà chez
     *   Pennylane → avertissement + confirmation, jamais un blocage.
     *
     * Typologie (même règle que le backend) : B2C ssi
     * `client.__t === 'Contact' && client.type === 'individual'`, sinon B2B.
     * Les gardes numéro (émissibilité)/sentToPennylane ne sont PAS dupliquées
     * ici : déjà couvertes par l'éligibilité des appelants (bouton/filtre bulk).
     *
     * @param invoice facture Kilo avec `client` peuplé (objet Entity)
     * @return {{ blocking: (string|null), warnings: string[] }} libellés FR
     *   prêts à afficher
     */
    function checkInvoiceEmittable (invoice) {
      const client = invoice && invoice.client
      if (!client || typeof client !== 'object') {
        return { blocking: 'La facture n\'a pas de client', warnings: [] }
      }
      const warnings = []
      const isB2C = client.__t === 'Contact' && client.type === 'individual'
      if (isB2C) {
        if (isBlank(client.firstname) || isBlank(client.lastname)) {
          warnings.push('Prénom/nom du contact manquants')
        }
      } else if (isBlank(client.label)) {
        warnings.push('Nom du client manquant')
      }
      const address = client.address
      if (!address || typeof address !== 'object' ||
        isBlank(address.addressLine1) || isBlank(address.postalCode) || isBlank(address.city)) {
        warnings.push('Adresse du client incomplète (adresse, code postal ou ville)')
      }
      return { blocking: null, warnings: warnings }
    }

    /** Champ requis manquant = absent, ou chaîne vide/blanche. */
    function isBlank (value) {
      return !value || !String(value).trim()
    }

    // ── Statut e-invoicing plateforme (PA) & renvoi ──────────────────────────

    /**
     * Statut e-invoicing d'une facture Kilo chez la plateforme (PA), normalisé
     * par le backend. Réponse : `{ synced, pennylaneInvoiceId?, status?,
     * eInvoicingStatus?, eInvoicingReason?, eInvoicingReasonRaw? }` (`synced:false`
     * = facture pas encore transmise à Pennylane) — retourne directement
     * `res.data`.
     *
     * ⚠️ Appel de POLLING BACKGROUND sur la fiche facture : comme
     * getLastEmissionMonitoring, il ne doit JAMAIS déclencher le toast global
     * plein écran (broadcast `httpNotResponding` → `$rootScope.globalError`,
     * jamais réinitialisée = app masquée) ni la déconnexion (403 →
     * zlAuth.disconnect). L'intercepteur vérifie `ignoreErrors` par _.includes →
     * énumération exhaustive obligatoire, et LARGE (statut inattendu compris) ;
     * -1/0 couvrent l'échec réseau (AngularJS met status -1 backend down).
     * La promesse reste rejetée : l'appelant dégrade en « aucun affichage ».
     * @param kiloInvoiceId `_id` de la facture Kilo
     */
    function getEInvoicingStatusByKilo (kiloInvoiceId) {
      return $http({
        url: `${urls.backendPennylane}/invoices/kilo/${kiloInvoiceId}/e-invoicing-status`,
        method: 'GET',
        ignoreErrors: [-1, 0, 400, 401, 403, 404, 405, 408, 409, 410, 412, 422, 429, 500, 501, 502, 503, 504]
      }).then((res) => res.data)
    }

    /**
     * Renvoie une facture REJETÉE par la plateforme (PA) après correction des
     * données Kilo (l'archivage de l'ancienne facture se fait manuellement dans
     * l'UI Pennylane). Réponse : `{ monitoringId, status: 'pending' }`.
     *
     * Déclenché par un CLIC utilisateur (pas background) : les états
     * fonctionnels attendus sont affichés localement par une snack — on
     * supprime le toast global sur ces statuts (409 facture non rejetée/non
     * transmise, 422 sans numéro, 502/503 amont Kilo/Pennylane) mais la promesse
     * reste REJETÉE pour que l'appelant gère le message dédié. On ajoute aussi
     * les statuts INATTENDUS (-1/0 coupure réseau, 500 exception backend) pour
     * dégrader en snack locale plutôt que masquer l'app par le toast global.
     *
     * `options.force` : renvoi FORCÉ d'une facture déjà envoyée (bouton de la
     * fiche, confirmé par l'utilisateur) — body `{ force: true }` : le backend
     * lève ses gardes statut PA / `sentToPennylane` et ne réinitialise que son
     * Invoice locale ; le flag Kilo `sentToPennylane` est CONSERVÉ (l'info
     * « déjà envoyée » reste vraie). Doublon possible chez Pennylane si
     * l'ancienne facture n'y a pas été archivée : assumé par l'utilisateur.
     * Sans `force`, aucun body n'est envoyé (appel historique inchangé).
     * @param kiloInvoiceId `_id` de la facture Kilo
     * @param [options] `{ force: boolean }`
     */
    function reEmitInvoice (kiloInvoiceId, options) {
      const config = {
        url: `${urls.backendPennylane}/invoices/kilo/${kiloInvoiceId}/re-emit`,
        method: 'POST',
        ignoreErrors: [-1, 0, 409, 422, 500, 502, 503]
      }
      if (options && options.force === true) {
        config.data = { force: true }
      }
      return $http(config)
    }

    // ── Monitoring ───────────────────────────────────────────────────────────

    /**
     * Doc Monitoring d'un traitement asynchrone (scopé tenant côté backend).
     * Réponse : `{ status, info, kiloInvoiceRef, endedAt, ... }` — retourne
     * directement `res.data`.
     *
     * ⚠️ Appel de POLLING BACKGROUND (PennylaneEmissionTrackerService) : comme
     * getConnectedCompany, il ne doit JAMAIS déclencher le toast global plein
     * écran (broadcast `httpNotResponding` → `$rootScope.globalError`, jamais
     * réinitialisée = app masquée) ni la déconnexion (403 → zlAuth.disconnect).
     * L'intercepteur vérifie `ignoreErrors` par _.includes → énumération
     * exhaustive obligatoire, et LARGE : jusqu'à ~360 requêtes par suivi
     * (tick 5 s × timeout 30 min max), un statut inattendu (429 rate-limit,
     * 504 gateway…) ne doit jamais bricker l'app depuis un appel background ;
     * -1/0 couvrent l'échec réseau (AngularJS met status -1 backend down).
     * La promesse reste rejetée : l'appelant réessaie au tick suivant.
     */
    function getMonitoring (id) {
      return $http({
        url: `${urls.backendPennylane}/monitoring/${id}`,
        method: 'GET',
        ignoreErrors: [-1, 0, 400, 401, 403, 404, 405, 408, 409, 410, 412, 422, 429, 500, 501, 502, 503, 504]
      }).then((res) => res.data)
    }

    /**
     * Dernier Monitoring d'ÉMISSION d'une facture Kilo (tri createdAt desc +
     * limit 1 côté backend → le PLUS RÉCENT), ou `null` si aucun envoi tracé.
     * Sert à la consultation PASSIVE sur la fiche facture : échecs découverts
     * après coup (navigateur fermé pendant le suivi, dialog raté, autre
     * utilisateur), en complément du polling post-clic (tracker ci-dessus).
     *
     * ⚠️ Appel BACKGROUND au chargement de la fiche facture : comme
     * getMonitoring ci-dessus (voir son commentaire), il ne doit JAMAIS
     * déclencher le toast global plein écran ni la déconnexion → énumération
     * exhaustive de `ignoreErrors` obligatoire, LARGE (statut inattendu
     * compris) ; -1/0 couvrent l'échec réseau (backend down). La promesse
     * reste rejetée : l'appelant dégrade en « aucun affichage ».
     * @param kiloInvoiceId `_id` de la facture Kilo (= `kiloInvoiceRef` du Monitoring)
     */
    function getLastEmissionMonitoring (kiloInvoiceId) {
      return $http({
        url: `${urls.backendPennylane}/monitoring`,
        method: 'GET',
        params: { type: 'emission', kiloInvoiceRef: kiloInvoiceId, limit: 1 },
        ignoreErrors: [-1, 0, 400, 401, 403, 404, 405, 408, 409, 410, 412, 422, 429, 500, 501, 502, 503, 504]
      }).then((res) => (res.data && res.data[0]) || null)
    }
  }
})(window.angular, window._)
